import {
  RequirementListRequest as RequirementListRequestDto,
  RequirementPageResponse,
  RequirementRecordResponse,
  RequirementRequest as RequirementRequestDto,
  RequirementStatusesResponse,
  RequirementSummaryResponse,
  RequirementViewResponse,
} from "lib-klab-sdks";
import {
  Requirement, RequirementListRequest, RequirementPage, RequirementRecord,
  RequirementRequest, RequirementStatuses, RequirementSummary,
} from "../../../domain/models/requirement/requirement.model";

export function mapRequirementRecord(source: RequirementRecordResponse): RequirementRecord {
  return {
    id: source.requirementId,
    code: source.code,
    companyId: source.companyId,
    departmentId: source.departmentId,
    requesterUserId: source.requesterUserId,
    paymentDestination: source.paymentDestination,
    sourceType: source.sourceType,
    purchaseOrderId: source.purchaseOrderId,
    supplyRequestId: source.supplyRequestId,
    supplierId: source.supplierId,
    bankAccountId: source.bankAccountId,
    beneficiaryDescription: source.beneficiaryDescription,
    isForEvent: source.isForEvent,
    eventName: source.eventName,
    idCurrency: source.idCurrency,
    amount: source.amount,
    exchangeRate: source.exchangeRate,
    amountPen: source.amountPen,
    expenseConcept: source.expenseConcept,
    expenseCategoryId: source.expenseCategoryId,
    requestedPriorityId: source.requestedPriorityId,
    priorityLevelId: source.priorityLevelId,
    priorityReason: source.priorityReason,
    requirementType: source.requirementType,
    pendingDocumentReason: source.pendingDocumentReason,
    supportDocumentTypeId: source.supportDocumentTypeId,
    documentTypeDetail: source.documentTypeDetail,
    requestedDocumentDueDate: source.requestedDocumentDueDate,
    requiresCeoApproval: source.requiresCeoApproval,
    requirementStatusId: source.requirementStatusId,
    paymentStatusId: source.paymentStatusId,
    documentStatusId: source.documentStatusId,
    submittedAt: source.submittedAt,
    approvedAt: source.approvedAt,
    documentDueDate: source.documentDueDate,
    paidAt: source.paidAt,
    createdAt: source.createdAt,
    updatedAt: source.updatedAt,
  };
}

export function mapRequirement(source: RequirementViewResponse): Requirement {
  return {
    ...mapRequirementRecord(source),
    company: { id: source.company.id, code: source.company.code, name: source.company.name },
    department: source.department ? { id: source.department.id, code: source.department.code, name: source.department.name } : null,
    supplier: source.supplier ? {
      id: source.supplier.id,
      code: source.supplier.code,
      name: source.supplier.name,
      documentNumber: source.supplier.documentNumber,
    } : null,
    bankAccount: source.bankAccount ? {
      id: source.bankAccount.id,
      bankName: source.bankAccount.bankName,
      accountNumber: source.bankAccount.accountNumber,
      alias: source.bankAccount.alias,
      accountHolder: source.bankAccount.accountHolder,
    } : null,
    currency: {
      id: source.currency.id,
      code: source.currency.code,
      name: source.currency.name,
      symbol: source.currency.symbol,
    },
    priorityLevel: {
      id: source.priorityLevel.id,
      code: source.priorityLevel.code,
      name: source.priorityLevel.name,
      colorHex: source.priorityLevel.colorHex,
    },
    requester: source.requester ? {
      userId: source.requester.userId,
      firstName: source.requester.firstName,
      lastName: source.requester.lastName,
    } : null,
    requesterDepartment: source.requesterDepartment ? {
      id: source.requesterDepartment.id,
      code: source.requesterDepartment.code,
      name: source.requesterDepartment.name,
    } : null,
    requirementStatus: { id: source.requirementStatus.id, code: source.requirementStatus.code, name: source.requirementStatus.name },
    paymentStatus: { id: source.paymentStatus.id, code: source.paymentStatus.code, name: source.paymentStatus.name },
    documentStatus: { id: source.documentStatus.id, code: source.documentStatus.code, name: source.documentStatus.name },
    documentType: source.documentType ? {
      id: source.documentType.id,
      code: source.documentType.code,
      name: source.documentType.name,
    } : null,
  };
}

export function mapRequirementRequest(request: RequirementRequest): RequirementRequestDto {
  return {
    companyId: request.companyId,
    departmentId: request.departmentId,
    paymentDestination: request.paymentDestination,
    sourceType: request.sourceType,
    purchaseOrderId: request.purchaseOrderId,
    supplyRequestId: request.supplyRequestId,
    supplierId: request.supplierId,
    bankAccountId: request.bankAccountId,
    beneficiaryDescription: request.beneficiaryDescription,
    isForEvent: request.isForEvent,
    eventName: request.eventName,
    idCurrency: request.idCurrency,
    amount: request.amount,
    exchangeRate: request.exchangeRate,
    expenseConcept: request.expenseConcept,
    expenseCategoryId: request.expenseCategoryId,
    priorityLevelId: request.priorityLevelId,
    priorityReason: request.priorityReason,
    requirementType: request.requirementType,
    pendingDocumentReason: request.pendingDocumentReason,
    supportDocumentTypeId: request.supportDocumentTypeId,
    documentTypeDetail: request.documentTypeDetail,
    requestedDocumentDueDate: request.requestedDocumentDueDate,
  };
}

export function mapRequirementListRequest(request: RequirementListRequest): RequirementListRequestDto {
  return {
    companyId: request.companyId,
    departmentId: request.departmentId,
    requesterDepartmentId: request.requesterDepartmentId,
    requesterUserId: request.requesterUserId,
    supplierId: request.supplierId,
    requirementStatusId: request.requirementStatusId,
    requirementStatus: request.requirementStatus,
    paymentStatus: request.paymentStatus,
    documentStatus: request.documentStatus,
    documentStatuses: request.documentStatuses,
    paymentDestination: request.paymentDestination,
    priorityLevelId: request.priorityLevelId,
    createdFrom: request.createdFrom,
    createdTo: request.createdTo,
    dueFrom: request.dueFrom,
    dueTo: request.dueTo,
    search: request.search,
    requesterName: request.requesterName,
    limit: request.limit,
    offset: request.offset,
  };
}

export function mapRequirementPage(source: RequirementPageResponse): RequirementPage {
  return {
    items: source.items.map(mapRequirement),
    limit: source.limit,
    offset: source.offset,
    hasNext: source.hasNext,
    total: source.total,
  };
}

export function mapRequirementStatuses(source: RequirementStatusesResponse): RequirementStatuses {
  const mapReference = (item: RequirementStatusesResponse["requirement"][number]) => ({
    id: item.id, code: item.code, name: item.name, symbol: item.symbol,
  });
  return {
    requirement: source.requirement.map(mapReference),
    payment: source.payment.map(mapReference),
    document: source.document.map(mapReference),
    approval: source.approval.map(mapReference),
  };
}

export function mapRequirementSummary(source: RequirementSummaryResponse): RequirementSummary {
  const amounts = (value: { readonly pen: number; readonly usd: number }) => ({ pen: value.pen, usd: value.usd });
  return {
    kpis: {
      pendingAmount: amounts(source.kpis.pendingAmount),
      pendingPaymentsCount: source.kpis.pendingPaymentsCount,
      overdueDocumentsCount: source.kpis.overdueDocumentsCount,
      paidThisMonthCount: source.kpis.paidThisMonthCount,
      pendingDocumentsCount: source.kpis.pendingDocumentsCount,
      onTimeDocumentsCount: source.kpis.onTimeDocumentsCount,
      dueSoonDocumentsCount: source.kpis.dueSoonDocumentsCount,
      extensionRequestsCount: source.kpis.extensionRequestsCount,
    },
    month: {
      month: source.month.month,
      requested: amounts(source.month.requested),
      paid: amounts(source.month.paid),
      pending: amounts(source.month.pending),
      overdue: amounts(source.month.overdue),
    },
  };
}
